import { useCallback, useEffect, useRef, useState } from 'react';
import { isComposingEvent } from '@frameio/vapor';

function getOptions(listEl: HTMLElement): HTMLButtonElement[] {
  return Array.from(
    listEl.querySelectorAll<HTMLButtonElement>(
      'li[role="option"] > button:not([disabled])'
    )
  );
}

export const useKeyInteraction = (controlledByElement: HTMLElement) => {
  const ref = useRef<HTMLElement>(undefined);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const getActiveItemId = useCallback(() => {
    return (
      window?.document.activeElement?.parentElement?.getAttribute('id') ?? null
    );
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isComposingEvent(e)) return;

      const refEl = ref.current as HTMLElement;
      const activeEl = window?.document.activeElement;
      const focusIsInsideList = refEl === activeEl || refEl.contains(activeEl);
      const canUseDownArrow =
        focusIsInsideList || activeEl === controlledByElement;

      if (
        (e.key === 'ArrowDown' && canUseDownArrow) ||
        (e.key === 'ArrowRight' && focusIsInsideList)
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();

        const options = getOptions(refEl);
        const currentIndex = options.findIndex(
          (o) => o === activeEl || o.contains(activeEl as Node)
        );
        options[currentIndex + 1]?.focus();
        setActiveItemId(getActiveItemId());
      }

      if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft') && focusIsInsideList) {
        e.preventDefault();
        e.stopImmediatePropagation();

        const options = getOptions(refEl);
        const currentIndex = options.findIndex(
          (o) => o === activeEl || o.contains(activeEl as Node)
        );

        if (currentIndex <= 0) {
          controlledByElement.focus();
          setActiveItemId(null);
        } else {
          options[currentIndex - 1]?.focus();
          setActiveItemId(getActiveItemId());
        }
      }
    },
    [controlledByElement, getActiveItemId]
  );

  const handleFocusChange = useCallback(() => {
    setActiveItemId(getActiveItemId());
  }, [getActiveItemId]);

  const handleFocusOut = useCallback(() => {
    setActiveItemId(null);
  }, []);

  useEffect(() => {
    const refEl = ref.current as HTMLElement;
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    refEl.addEventListener('focusin', handleFocusChange, { capture: true });
    refEl.addEventListener('focusout', handleFocusOut, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      refEl.removeEventListener('focusin', handleFocusChange, {
        capture: true,
      });
      refEl.removeEventListener('focusout', handleFocusOut, {
        capture: true,
      });
    };
  }, [controlledByElement, handleFocusChange, handleFocusOut, handleKeyDown]);

  return [ref, activeItemId] as const;
};
